import {
  Activator,
  Card,
  Content,
  ContentCard,
  Control,
  Functionality,
  HighlightCard,
  HTML,
  List,
  MediaType,
  Navigator,
  Operator,
  Pointer,
  Reference,
} from '@yleisradio/areena-types';
import {
  EditableProfileField,
  MediaApiResponse,
  SubtitleBackgroundOption,
  SubtitleOption,
  SubtitleSizeOption,
  LoginSubRoute,
} from './appTypings';
import { ExtraArguments } from './configureStore';
import { KeyboardKey } from './keyboard/squareKeys';
import {
  LoggedInUser,
  ParentProfile,
  Profile,
  ProfileIcon,
  ProfileIconImage,
  SubProfile,
} from '../common/types';

export function isActivator(control: Control): control is Activator {
  return control.type === 'activator' && 'functionality' in control;
}

export function isNavigator(to: Control | undefined | null): to is Navigator {
  return to?.type === 'navigator' && 'destination' in to;
}

export function isObjOperator(obj: any): obj is Operator {
  return obj?.type === 'operator' && 'operation' in obj;
}

export function isOperator(
  control: Control | undefined | null
): control is Operator {
  return control?.type === 'operator' && 'operation' in control;
}

export function isReference(to: Control): to is Reference {
  return to.type === 'reference' && 'destination' in to;
}

export function isPointer(
  obj: Pointer | Functionality | Navigator
): obj is Pointer {
  return 'uri' in obj;
}

export function isFunctionality(obj: unknown): obj is Functionality {
  return typeof obj === 'object' && !!obj && 'id' in obj;
}

export function isList(content: Content | undefined): content is List {
  return content?.type === 'list';
}

export function isHTML(content: Content | undefined): content is HTML {
  return content?.type === 'html';
}

export function isHighlightCard(
  content: Content | undefined
): content is HighlightCard {
  return content?.type === 'highlightCard';
}

export function isContentCard(
  content: Content | undefined
): content is ContentCard {
  return content?.type === 'contentCard';
}

export function isCard(obj: unknown): obj is Card {
  return (
    typeof obj === 'object' && !!obj && 'type' in obj && obj.type === 'card'
  );
}

export function isContentKnown(
  content: Content | undefined
): content is ContentCard | HighlightCard | HTML | List {
  if (!content) return false;

  return (
    isFunctionality(content) ||
    isList(content) ||
    isHighlightCard(content) ||
    isContentCard(content)
  );
}

export function isSubtitleSizeOption(
  option: SubtitleOption
): option is SubtitleSizeOption {
  return option === 'small' || option === 'normal' || option === 'large';
}

export function isSubtitleBackgroundOption(
  option: SubtitleOption
): option is SubtitleBackgroundOption {
  return option === 'black' || option === 'white' || option === 'transparent';
}

export function isMediaItem(obj: unknown): obj is MediaApiResponse {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  const mediaItem =
    ('hls' in obj ? obj.hls : undefined) ||
    ('dash' in obj ? obj.dash : undefined) ||
    ('cmaf-hls' in obj ? obj['cmaf-hls'] : undefined) ||
    ('pmd' in obj && Array.isArray(obj.pmd) ? obj.pmd[0] : undefined);

  if (mediaItem) {
    const mediaTypes: MediaType[] = ['VideoObject', 'AudioObject'];
    return mediaTypes.includes(mediaItem.type);
  }

  return false;
}

export function isExtraArguments(obj: unknown): obj is ExtraArguments {
  return (
    typeof obj === 'object' &&
    !!obj &&
    'authFetch' in obj &&
    'appFeatures' in obj
  );
}

export function isProfileIconImage(
  icon: ProfileIcon
): icon is ProfileIconImage {
  return icon.type === 'image';
}

export function isSubProfile(profile: Profile): profile is SubProfile {
  return profile.parentId !== null;
}

export function isParentProfile(profile: Profile): profile is ParentProfile {
  return profile.parentId === null;
}

export function isEditableProfileField(
  field: string
): field is EditableProfileField {
  return ['displayName', 'icon'].includes(field);
}

export function isKeyboardKey(maybeKey: unknown): maybeKey is KeyboardKey {
  return typeof maybeKey === 'object' && !!maybeKey && 'value' in maybeKey;
}

export function isKeyboardKeys(
  maybeKeys: unknown[]
): maybeKeys is KeyboardKey[] {
  return maybeKeys.every(isKeyboardKey);
}

export function isLoginSubRoute(subRoute: string): subRoute is LoginSubRoute {
  return (
    subRoute === 'pair' || subRoute === 'credentials' || subRoute === 'register'
  );
}

export function isLoggedInUser(maybeUser: unknown): maybeUser is LoggedInUser {
  return typeof maybeUser === 'object' && !!maybeUser && 'userId' in maybeUser;
}
